// Main-thread client for the device Service Worker.
//
// Why a Service Worker: it survives navigation within a single tab
// (which SharedWorker didn't, reliably) and persists across tab
// closures as long as any tab of the origin stays open OR the
// browser keeps the SW warm. Lock happens on explicit user action,
// on the SW being evicted by the browser (idle / memory pressure /
// browser restart).
//
// Communication uses MessageChannel per-request so we don't have to
// multiplex correlation ids over a single message port — each
// request allocates a fresh channel, waits on its reply, and drops.

import type { WorkerRequest, WorkerResponse } from './deviceWorkerProtocol'
import { DEVICE_WORKER_NAME } from './deviceWorkerProtocol'

const SW_URL = '/practicemd-device-sw.js'

let registerPromise: Promise<ServiceWorkerRegistration> | null = null
let nextId = 1

async function ensureRegistered(): Promise<ServiceWorker> {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
        throw new Error('device worker: Service Workers are not available in this environment')
    }
    if (!registerPromise) {
        registerPromise = navigator.serviceWorker.register(SW_URL, { scope: '/' }).catch(err => {
            registerPromise = null // allow retry on next call
            throw err
        })
    }
    await registerPromise
    // `navigator.serviceWorker.ready` waits for an activated SW
    // controlling this page or (on first install) the activation to
    // complete. Combined with skipWaiting()+clients.claim() inside
    // the SW, this is enough to give us a controller to post to.
    await navigator.serviceWorker.ready
    if (!navigator.serviceWorker.controller) {
        // First-install race: activation completed but this page
        // isn't yet claimed. Wait for the claim to land.
        await new Promise<void>(resolve => {
            const handler = () => {
                navigator.serviceWorker.removeEventListener('controllerchange', handler)
                resolve()
            }
            navigator.serviceWorker.addEventListener('controllerchange', handler)
        })
    }
    const controller = navigator.serviceWorker.controller
    if (!controller) {
        throw new Error('device worker: could not obtain a service worker controller')
    }
    return controller
}

function request<T = any>(msg: Omit<WorkerRequest, 'id'>): Promise<T> {
    return new Promise<T>(async (resolve, reject) => {
        let controller: ServiceWorker
        try {
            controller = await ensureRegistered()
        } catch (e) {
            reject(e)
            return
        }
        const id = nextId++
        const full = { ...msg, id } as WorkerRequest
        const channel = new MessageChannel()
        channel.port1.onmessage = (ev: MessageEvent) => {
            const resp = ev.data as WorkerResponse
            channel.port1.close()
            if (resp.ok) resolve(resp.data as T)
            else reject(new Error(resp.error))
        }
        try {
            controller.postMessage(full, [channel.port2])
        } catch (e) {
            channel.port1.close()
            reject(e)
        }
    })
}

// Sanity-check that the SW we're talking to implements our expected
// protocol version. Called opportunistically — if the check fails,
// the Service Worker is from an older incompatible build and needs
// to be replaced; tell the caller to hard-reload.
async function assertProtocol(): Promise<void> {
    const name = await request<string>({ cmd: 'ping' })
    if (name !== DEVICE_WORKER_NAME) {
        throw new Error(
            `device worker: protocol mismatch (SW reports ${name}, expected ${DEVICE_WORKER_NAME}). Reload the page.`
        )
    }
}

export const deviceWorkerClient = {
    unlock: (masterKeyHex: string) => request<void>({ cmd: 'unlock', masterKeyHex }),
    decryptWithMasterKey: (envelopeB64: string) =>
        request<string>({ cmd: 'decryptWithMasterKey', envelopeB64 }),
    encryptWithMasterKey: (plaintextB64: string) =>
        request<string>({ cmd: 'encryptWithMasterKey', plaintextB64 }),
    isUnlocked: () => request<boolean>({ cmd: 'isUnlocked' }),
    lock: () => request<void>({ cmd: 'lock' }),
    // Exposed for diagnostics — main-thread code generally doesn't
    // need to call this directly.
    assertProtocol,
}
